/* eslint-disable max-len */
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import {
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";

import { Box, Stack } from "@mui/material";

import {
  Pagination,
  PaginationItem,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useEffect, useState } from "react";

const ALL_PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100, 200, 300];

export const DataTableClientSideMembers = (props) => {
  const apiRef = useGridApiRef();
  const { columns: propColumns, defaultGrid, rows: propRows, loading } = props;

  const [state, setState] = useState(() => {
    const initialRows = propRows && propRows.length > 0 ? propRows : [];
    const initialPageSize =
      defaultGrid?.pagehead === "PDC"
        ? 20
        : defaultGrid?.pagehead === "pdcfileupload" ||
          defaultGrid?.pagehead === "productfileupload"
        ? 300
        : 10;
    return {
      isLoading: false,
      data: initialRows, // Full dataset
      total: initialRows.length, // Total count from the full dataset
      page: 1, // Current page (1-indexed for UI)
      pageSize: ALL_PAGE_SIZE_OPTIONS.includes(initialPageSize)
        ? initialPageSize
        : ALL_PAGE_SIZE_OPTIONS[0],
      columns: propColumns || [],
    };
  });

  // Effect to handle changes in incoming rows
  useEffect(() => {
    const newRows = propRows && propRows.length > 0 ? propRows : [];
    apiRef.current.updateRows(newRows); // Inform DataGrid about new rows
    setState((prevState) => ({
      ...prevState,
      data: newRows,
      total: newRows.length,
      page: 1, // Reset to page 1 when data changes
    }));
    // Ensure DataGrid also resets to its first page (0-indexed)
    // This is important if the new dataset has fewer pages or if we want a clean reset
    if (apiRef.current?.setPage) {
      // Check if apiRef is initialized
      apiRef.current.setPage(0);
    }
  }, [propRows]);

  // Effect to handle changes in incoming columns
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      columns: propColumns || [],
    }));
  }, [propColumns]);

  function CustomPagination() {
    const gridApiRef = useGridApiContext(); // Use the context's apiRef for selectors
    const currentPageZeroIndexed = useGridSelector(
      gridApiRef,
      gridPageSelector
    );
    const pageCount = useGridSelector(gridApiRef, gridPageCountSelector);

    const handlePageSizeChange = (event) => {
      const newPageSize = Number(event.target.value);
      setState((prevState) => ({
        ...prevState,
        pageSize: newPageSize,
        page: 1, // Reset to page 1 when page size changes
      }));
      gridApiRef.current.setPage(0); // Tell DataGrid to go to its first page (0-indexed)
      // DataGridPro will also be updated with the new pageSize prop from parent state
    };

    const handlePageChange = (event, newPageOneIndexed) => {
      // newPageOneIndexed comes from MUI Pagination (1-indexed)
      gridApiRef.current.setPage(newPageOneIndexed - 1);
      // DataGridPro's onPageChange will then update the parent state.page
    };

    return (
      <>
        <div className="grid-pagiation">
          <Box className="customRowPerPageBox" component="div">
            <div>View</div>
            <Box component="div" sx={{ marginX: "10px" }}>
              <FormControl>
                <Select
                  size="small"
                  id="row-per-page"
                  onChange={handlePageSizeChange}
                  value={state.pageSize} // Controlled by DataTableClientSide's state
                  aria-describedby={"row-per-page"}
                >
                  {ALL_PAGE_SIZE_OPTIONS.map((value) => (
                    <MenuItem value={value} key={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <div>rows per page</div>
          </Box>
          <Pagination
            variant="outlined"
            shape="rounded"
            count={pageCount} // Total pages from DataGridPro
            page={currentPageZeroIndexed + 1} // Current page from DataGridPro (converted to 1-indexed)
            onChange={handlePageChange}
            renderItem={(item) => (
              <PaginationItem
                components={{
                  previous: KeyboardArrowLeftIcon,
                  next: KeyboardArrowRightIcon,
                }}
                {...item}
              />
            )}
          />
        </div>
      </>
    );
  }

  const entriesStart =
    state.total > 0 ? state.pageSize * (state.page - 1) + 1 : 0;
  const entriesEnd =
    state.total > 0 ? Math.min(state.total, state.pageSize * state.page) : 0;

  return (
    <>
      <Box
        component="div"
        className="data-per-row-message"
        sx={{ paddingBottom: "15px" }}
      >
        Showing {entriesStart} to {entriesEnd} out of {state.total} entries
      </Box>

      <div style={{ height: "468px", width: "100%" }}>
        <DataGridPro
          apiRef={apiRef}
          rows={state.data} // Full dataset
          columns={state.columns}
          // autoHeight={true}
          disableColumnMenu={true}
          loading={loading} // Pass the loading prop directly to DataGridPro
          rowCount={state.total} // Total number of items
          // loading={state.isLoading}

          pagination // Enable DataGridPro's pagination logic
          pageSize={state.pageSize} // Controlled by state
          page={state.page - 1} // Controlled by state (0-indexed for DataGridPro)
          rowsPerPageOptions={ALL_PAGE_SIZE_OPTIONS} // Inform DataGrid of all available options
          onPageChange={(newPageZeroIndexed) => {
            setState((prevState) => ({
              ...prevState,
              page: newPageZeroIndexed + 1,
            }));
          }}
          components={{
            Pagination: CustomPagination,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Result
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {defaultGrid?.keyPressSearch
                  ? "No results found. Please select search criteria to see results"
                  : "No Result"}
              </Stack>
            ),
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "" : "oddRow"
          }
          initialState={{ pinnedColumns: { right: ["action"] } }} // Assuming 'action' is a columnId
          getRowHeight={() => {
            if (defaultGrid?.rowHeight) {
              return "auto";
            }
            // return undefined; // or a default height if not 'auto'
          }}
          getRowId={(row) =>
            row.supplierProductCode
              ? defaultGrid?.pagehead === "pdcfileupload"
                ? row.id
                : row.supplierProductCode
              : row.id
          }
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#F0F0F0",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "600",
              fontSize: "14px",
              color: "#15191E",
            },
            "& .MuiDataGrid-row.oddRow": {
              backgroundColor: "#FBFBFB",
            },
            "& .MuiDataGrid-row.Mui-hovered, .MuiDataGrid-row.Mui-selected, .MuiDataGrid-row.Mui-selected.Mui-hovered":
              {
                backgroundColor: "#FFF5E1",
              },
            "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              borderRight: `1px solid #E6E6E6`,
              borderBottom: `1px solid #E6E6E6`,
            },
            "& .MuiDataGrid-cell": {
              fontWeight: "500",
            },
          }}
        />
      </div>
    </>
  );
};
